import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { useCallback } from 'react';
import useSessionStore from '../hooks/useSessionStore';
import Sizes from '../definitions/Sizes';
import ZIndex from '../definitions/ZIndex';
import useForm from '../hooks/useForm';
import AccordionFilters from './AccordionFilters';
import Fieldset from './Fieldset';
import FormComputedAsync from './FormComputedAsync';
import CircleSpinner from './CircleSpinner';
import HiddenField from './HiddenField';
import Form from './Form';


function FilteredAsyncContainer({
    primaryFilters,
    additionalFilters,
    areAdditionalFiltersHidden,
    columnCount,

    fetchedValueName,
    initialFetchedValue,

    onlyFetchWhen,
    getFormValues,
    onFetchValue,
    debounce,

    hasBorderAroundChildren,
    className,
    children,

    ...otherProps
}) {
    const sessionStore = useSessionStore();
    const formState = useForm();
    const [ fieldsetState, setFieldsetState ] = useState(null);

    if (!formState) {
        throw new Error('FilteredAsyncContainer requires a parent Form.');
    }


    const nameOfHasFetchedSession = `${fetchedValueName}HasFetchedSession`;

    /* Once our fieldset has .values we can apply that here as a default */
    const handleGetFormValues = useCallback((values, form) => {
        return {
            ...getFormValues(values, form),
            [nameOfHasFetchedSession]: values[nameOfHasFetchedSession],
        };
    }, [ getFormValues, nameOfHasFetchedSession ]);


    const handleOnlyFetchWhen = useCallback((values, theFormState) => {
        return values?.[nameOfHasFetchedSession] && (onlyFetchWhen?.(values, theFormState) ?? fieldsetState?.isValid);
    }, [ onlyFetchWhen, nameOfHasFetchedSession, fieldsetState ]);


    return (
        <Container
            className={classNames(className, 'rh-border-radius-4px', {
                'rh-b-1px-solid-stone': hasBorderAroundChildren,
            })}
            {...otherProps}
        >
            <Fieldset
                className="filters-container rh-mx-0"
                onCreateState={setFieldsetState}
            >
                <AccordionFilters
                    className={classNames('filters', {
                        'rh-bb-1px-solid-stone': hasBorderAroundChildren,
                        'rh-b-1px-solid-stone rh-mb-1 rh-border-radius-4px': !hasBorderAroundChildren,
                    })}
                    primaryFilters={primaryFilters}
                    additionalFilters={additionalFilters}
                    areAdditionalFiltersHidden={areAdditionalFiltersHidden}
                    columnCount={columnCount}
                />

                <HiddenField
                    name={nameOfHasFetchedSession}
                    value={sessionStore.hasFetchedSession}
                />
            </Fieldset>


            <FormComputedAsync
                name={fetchedValueName}

                onlyWhen={handleOnlyFetchWhen}
                getFormValues={handleGetFormValues}
                onFetchValue={onFetchValue}
                initialValue={initialFetchedValue}
                debounce={debounce}
            >
                {(values, theFormState) => (
                    <div className="items-container">
                        <If condition={!values?.[`${fetchedValueName}HasFetched`] || values?.[`${fetchedValueName}IsFetching`]}>
                            {/* Set the z-index to smaller than drop downs since they
                                could potentially be inside the filters  */}
                            <CircleSpinner zIndex={ZIndex.ELEMENTS} />
                        </If>

                        <Choose>
                            <When condition={values?.[`${fetchedValueName}Error`]}>
                                <p className="rh-text-m rh-py-5 rh-text-align-center">
                                    <FormattedMessage
                                        id="base-ui.FilteredAsyncContainer.errorMessage"
                                        defaultMessage="Oops, something went wrong. Please refresh the page and try again."
                                    />
                                </p>
                            </When>
                            <Otherwise>
                                {children(values, theFormState)}
                            </Otherwise>
                        </Choose>
                    </div>
                )}
            </FormComputedAsync>
        </Container>
    );
}

FilteredAsyncContainer.propTypes = {
    primaryFilters: PropTypes.arrayOf(
        PropTypes.node,
    ).isRequired,
    additionalFilters: PropTypes.arrayOf(
        PropTypes.node,
    ),
    areAdditionalFiltersHidden: PropTypes.bool,
    columnCount: PropTypes.number,

    fetchedValueName: PropTypes.string.isRequired,
    initialFetchedValue: PropTypes.any,

    onlyFetchWhen: PropTypes.func,
    // Fieldset doesn't currently have .values so this can't be defaulted
    getFormValues: PropTypes.func.isRequired,
    onFetchValue: PropTypes.func.isRequired,
    debounce: PropTypes.number,

    hasBorderAroundChildren: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.func.isRequired,
};

FilteredAsyncContainer.defaultProps = {
    additionalFilters: [],
    areAdditionalFiltersHidden: false,
    columnCount: undefined,
    initialFetchedValue: undefined,
    onlyFetchWhen: undefined,
    debounce: 800,
    hasBorderAroundChildren: true,
    className: undefined,
};


const Container = styled.div`
    > .filters-container {
        > .filters {
            /* since this has a background colour, top corners must share the same radius as outer-most container */
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
        }
    }

    > .items-container {
        min-height: ${Sizes.SPACING.EIGHT}; /* Set for when we have a spinner/error message and empty children */
        position: relative;

        /* Adding 1px padding to the bottom to add room
            for the border radius. Otherwise we'd have to apply
            border radius on all bottom components for the table. */
        padding-bottom: 1px;
    }
`;

// Automatically encapsulate in Form if no parent form is detected.
// Use case: Modals that contain a FilteredAsyncContainer
function withForm(Component) {
    return function WithForm(props) {
        const form = useForm();

        return (
            <Choose>
                <When condition={form != null}>
                    <Component {...props} />
                </When>
                <Otherwise>
                    <Form>
                        <Component {...props} />
                    </Form>
                </Otherwise>
            </Choose>
        );
    };
}

export default withForm(observer(FilteredAsyncContainer));
